import React from "react";
import { GoogleLogin } from "@react-oauth/google";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { clientId } from "../const/env";
import { GoogleAuthProvider, signInWithCredential } from "firebase/auth";
import { auth, db } from "../firebase/firebase";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { API_BASE_URL } from "../api/backendConfig";

export const fetchGoalsBookCollection = async (): Promise<unknown[]> => {
  try {
    const goalsBookRef = collection(db, "Goals_book");
    const goalsBookSnapshot = await getDocs(goalsBookRef);
    
    const goalsBookData: unknown[] = [];
    goalsBookSnapshot.forEach((docSnap) => {
      const data = {
        id: docSnap.id,
        ...docSnap.data()
      };
      goalsBookData.push(data);
    });
    
    return goalsBookData;
  } catch (error) {
    console.error("[Test] Error fetching Goals_book collection:", error);
    throw error;
  }
};

export const testTabbedDictAccess = async (): Promise<void> => {
  try {
    const currentUser = auth?.currentUser;
    if (!currentUser) {
      return;
    }

    const wRefDoc = doc(db, "tabbed_dict", "admin", currentUser.uid, "data");
    const jRefDoc = doc(db, "tabbed_dict", "admin", "anzHKVkexIZx0oakVGcty3oqw4j1", "data");
    await getDoc(wRefDoc);
    await getDoc(jRefDoc);
  } catch (error) {
    console.error("[Test] Error in tabbed_dict access test:", error);
  }
};

if (typeof window !== 'undefined') {
  (window as Window & { testFetchGoalsBook?: typeof fetchGoalsBookCollection }).testFetchGoalsBook = fetchGoalsBookCollection;
  (window as Window & { testTabbedDictAccess?: typeof testTabbedDictAccess }).testTabbedDictAccess = testTabbedDictAccess;
}

// Call FastAPI backend to store user login data
const notifyBackendOfLogin = async (
  firebaseUser: { uid: string; getIdToken: () => Promise<string> },
  userData: {
    uid: string;
    email?: string;
    name?: string;
    picture?: string;
    provider?: string;
  },
): Promise<void> => {
  try {
    const idToken = await firebaseUser.getIdToken();
    const response = await fetch(`${API_BASE_URL}/user/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        uid: userData.uid,
        id_token: idToken,
        email: userData.email || null,
        name: userData.name || null,
        photo_url: userData.picture || null,
        provider: userData.provider || "google",
        metadata: {
          user_agent: navigator.userAgent,
          platform: "web",
        },
      }),
    });

    if (!response.ok) {
      console.error("[Login] Backend login notification failed:", response.statusText);
      return;
    }

    const result = await response.json();
    console.log("[Login] Backend notified of login:", result);
  } catch (error) {
    // Don't block login if backend call fails
    console.error("[Login] Error notifying backend:", error);
  }
};

export const Login = (): React.ReactElement => {
  const handleGoogleLoginSuccess = async (credentialResponse: { credential?: string }): Promise<void> => {
    const { credential } = credentialResponse;
    if (credential) {
      try {
        const decoded = jwtDecode(credential) as { email?: string; name?: string; picture?: string; sub?: string };
        sessionStorage.setItem("user", JSON.stringify(decoded));

        const googleCredential = GoogleAuthProvider.credential(credential);
        const userCredential = await signInWithCredential(auth, googleCredential);

        await new Promise(resolve => setTimeout(resolve, 100));

        // Notify backend of successful login
        if (userCredential.user) {
          await notifyBackendOfLogin(userCredential.user, {
            uid: userCredential.user.uid,
            email: decoded.email || userCredential.user.email || undefined,
            name: decoded.name || userCredential.user.displayName || undefined,
            picture: decoded.picture || undefined,
            provider: "google",
          });
        }

        try {
          const adminsRef = collection(db, "tabbed_dict", "admin");
          await getDocs(adminsRef);
        } catch (error) {
          console.error("[Login] Error fetching admins collection:", error);
        }

        if (!sessionStorage.getItem("user")) {
          sessionStorage.setItem("user", JSON.stringify(decoded));
        }

        window.location.reload();
      } catch (error) {
        console.error("Error signing into Firebase:", error);
        if (!sessionStorage.getItem("user")) {
          const decoded = jwtDecode(credential) as { email?: string };
          sessionStorage.setItem("user", JSON.stringify(decoded));
        }
        window.location.reload();
      }
    }
  };

  return (
    <GoogleOAuthProvider clientId={clientId || ""}>
      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="bg-white border border-gray-200 rounded-3xl shadow-lg p-10 w-full max-w-md">
          <h1 className="text-3xl font-bold text-center text-gray-800 mb-6">
            Let's Get Started{" "}
          </h1>
          <p className="text-center text-gray-500 mb-8">
            Continue with your Google account to continue
          </p>
          <div className="flex justify-center">
            <GoogleLogin onSuccess={handleGoogleLoginSuccess} />
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};
